/**
 * Protected browser data belongs to an authenticated entitlement scope, not a
 * module's lifetime. The route boundary sets the scope before rendering readers.
 * Only the opaque epoch token is exposed; the caller's identity key stays local.
 */
let scopeKey: string | undefined;
let epoch = 0;
const listeners = new Set<() => void>();

export const protectedDataScope = (): string => `protected-data:${epoch}`;

export const setProtectedDataScope = (scope: string): string => {
  if (scopeKey === scope) return protectedDataScope();
  scopeKey = scope;
  epoch++;
  // Clear every cache synchronously before the boundary can render new children.
  // A failing subscriber must not prevent other protected caches from clearing.
  let failure: unknown;
  for (const listener of [...listeners]) {
    try { listener(); } catch (cause) { failure ??= cause; }
  }
  if (failure) throw failure;
  return protectedDataScope();
};

export const onProtectedDataScopeChange = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

export const assertProtectedDataScope = (token: string): void => {
  if (token !== protectedDataScope()) throw new DOMException('Protected data access changed.', 'AbortError');
};
